import FetchHelper from '../helpers/fetch_helper';
import Constants from '../helpers/constants';

const AdminFRDocumentsDispatcher = {
  countStepDocsAndFrsInTierView({ tier }) {
    try {
      const url = `admin/tiers-view-financing-request-step-docs/tiers/${tier}/counts?pageSize=100000`;
      return FetchHelper.fetchJson(url, { method: 'GET' });
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: { error, defaultErrorMessage: `Count not count Tier View Step Docs for tier ${tier}` },
      });
      return null;
    }
  },

  fetchFrDocuments({ sortOptions, filterOptions, tier, errorMessage }) {
    let promise;
    try {
      const url = `admin/tiers-view-financing-request-step-docs/tiers/${tier}?pageSize=250`;
      promise = FetchHelper.fetchJson(encodeURI(url), { method: 'GET' });
    } catch (error) {
      if (error.response && error.response.status === Constants.ERROR_CODE.FORBIDDEN) {
        this.dispatch({
          type: 'handleRequestError',
          data: { defaultErrorMessage: 'You do not have permission to view this page.' },
        });
      } else {
        this.dispatch({
          type: 'handleRequestError',
          data: { error, defaultErrorMessage: errorMessage },
        });
      }
    }
    return promise;
  },

  fetchFrDocumentsTier1({ sortOptions, filterOptions }) {
    return this.dispatch({
      type: 'fetchFrDocuments',
      data: {
        sortOptions,
        filterOptions,
        tier: 'tier_1',
        errorMessage: 'Could not retrieve financing request document records for tier 1.',
      },
    });
  },

  fetchFrDocumentsTier2({ sortOptions, filterOptions }) {
    return this.dispatch({
      type: 'fetchFrDocuments',
      data: {
        sortOptions,
        filterOptions,
        tier: 'tier_2',
        errorMessage: 'Could not retrieve financing request document records for tier 2.',
      },
    });
  },

  fetchFrDocumentsEscalated({ sortOptions, filterOptions }) {
    filterOptions.documentStatus = 'escalated';
    return this.dispatch({
      type: 'fetchFrDocuments',
      data: {
        sortOptions,
        filterOptions,
        tier: 'escalated',
        errorMessage: 'Could not retrieve financing request document records with escalated status.',
      },
    });
  },

  async fetchAdminFinancingRequest({ customerId }) {
    try {
      const data = await FetchHelper.fetchJson(`admin/customers/${customerId}/tiers`, {
        method: 'GET',
      });
      const adminFinancingRequest = data.results[0];
      this.setStoreValWithCheck('adminFinancingRequest', adminFinancingRequest);
    } catch (error) {
      if (error.response && error.response.status === Constants.ERROR_CODE.FORBIDDEN) {
        this.dispatch({
          type: 'handleRequestError',
          data: { defaultErrorMessage: 'You do not have permission to view this page.' },
        });
      } else {
        this.dispatch({
          type: 'handleRequestError',
          data: { error, defaultErrorMessage: 'Could not fetch admin financing request.' },
        });
      }
    }
  },

  async fetchAdminFinancingRequestHistory({ financingRequestId }) {
    try {
      const data = await FetchHelper.fetchJson(`admin/financing-requests/${financingRequestId}/history`, {
        method: 'GET',
      });
      const adminFinancingRequestHistory = data.results;
      this.setStoreVal('adminFinancingRequestHistory', adminFinancingRequestHistory);
    } catch (error) {
      if (error.response && error.response.status === Constants.ERROR_CODE.FORBIDDEN) {
        this.dispatch({
          type: 'handleRequestError',
          data: { defaultErrorMessage: 'You do not have permission to view this page.' },
        });
      } else {
        this.dispatch({
          type: 'handleRequestError',
          data: { error, defaultErrorMessage: 'Could not fetch admin financing request history.' },
        });
      }
    }
  },

  async fetchDecisions(docId) {
    let promise;
    try {
      promise = await FetchHelper.fetchJson(`admin/financing-request-step-docs/${docId}/decisions`, { method: 'GET' });
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: { error, defaultErrorMessage: 'Could not fetch admin financing request.' },
      });
    }

    return promise;
  },

  async submitDocDecision({ docId, decision, reason, customerId }) {
    let promise;
    try {
      const body = { decision, reason, tier: 'tier_1' };
      promise = await FetchHelper.fetchJson(`admin/financing-request-step-docs/${docId}/decisions`, {
        method: 'POST',
        body,
      });
      await this.dispatch({ type: 'fetchAdminFinancingRequest', data: { customerId } });
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: { error, defaultErrorMessage: 'The document could not be submitted for decision' },
      });
    }

    return promise;
  },

  async submitBulkDocDecision(requests) {
    let promise;
    try {
      const customerId = requests[0].customerId;
      const body = requests.map(({ docId, decision, reason }) => ({
        stepDocId: docId,
        decision,
        reason,
        tier: 'tier_1',
      }));

      promise = await FetchHelper.fetchJson(`admin/financing-request-step-docs/decisions`, {
        method: 'POST',
        body,
      });
      await this.dispatch({ type: 'fetchAdminFinancingRequest', data: { customerId } });
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: { error, defaultErrorMessage: 'The document could not be submitted for decision' },
      });
    }

    return promise;
  },

  async saveInternalNotes({ financingRequestId, stepId, docId, message }) {
    let promise;
    try {
      const body = { message, internal: true };
      promise = await FetchHelper.fetchJson(
        `financing-requests/${financingRequestId}/steps/${stepId}/docs/${docId}/discussions`,
        {
          method: 'POST',
          body,
        }
      );
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: { error, defaultErrorMessage: 'The note could not be submitted for document' },
      });
    }

    return promise;
  },

  async createAdminProposalMeasures({ financingRequestId, proposalMeasuresData }) {
    const adminFinancingRequest = this.getStoreVal('adminFinancingRequest');

    const userId = adminFinancingRequest.userId;
    const userName = adminFinancingRequest.userName;
    const impersonateHeader = btoa(`${userId}:${userName}`);

    const url = `admin/financing-requests/${financingRequestId}/measures`;
    let promise;
    try {
      promise = await FetchHelper.fetchJson(url, {
        method: 'POST',
        body: { measures: proposalMeasuresData },
        headers: { 'spruce-impersonate': impersonateHeader },
      });
      if (promise.results.length > 0) {
        this.setStoreVal('proposalMeasures', promise.results);
      } else {
        this.dispatch({
          type: 'handleRequestError',
          data: { promise, defaultErrorMessage: promise.fieldErrors[0] },
        });
      }
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: { error, defaultErrorMessage: 'Could not create proposal measures' },
      });
    }
    return promise;
  },

  async clearAdminFinancingRequest() {
    this.setStoreVal('adminFinancingRequest', null);
  },

  async fetchDocHistory(stepDocId) {
    let promise;
    try {
      promise = await FetchHelper.fetchJson(`admin/financing-request-step-docs/${stepDocId}/history`, {
        method: 'GET',
      });
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: { error, defaultErrorMessage: `Could not retrieve history for step doc id ${stepDocId}` },
      });
    }
    return promise;
  },

  async sendDocumentsForSignature({ financingRequestId, stepId, docsToSign }) {
    let promise = { results: undefined };
    try {
      const url = `admin/financing-requests/${financingRequestId}/steps/${stepId}/sign-documents`;
      promise = await FetchHelper.fetchJson(url, { method: 'POST', body: { docs: docsToSign } });
      if (promise.results.length < 0) {
        promise.results = [];
        this.dispatch({
          type: 'handleRequestError',
          data: { promise, defaultErrorMessage: promise.fieldErrors[0] },
        });
      }
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: { error, defaultErrorMessage: 'Could not send docs for signature' },
      });
    }
    return promise;
  },

  async sendForSMSSignature({ stepSku }) {
    let customer = this.getStoreVal('customer');
    let promise = { results: undefined };
    try {
      this.incrementLock('smssignaturelock');
      const url = `customers/${customer.id}/steps/${stepSku}/request-funding`;
      promise = await FetchHelper.fetchJson(url, { method: 'POST', version: '/v3', body: {} });
      if (promise.results.length < 0) {
        promise.results = [];
        this.dispatch({
          type: 'handleRequestError',
          data: { promise, defaultErrorMessage: promise.fieldErrors[0] },
        });
      }
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: { error, defaultErrorMessage: 'Could not send docs for signature' },
      });
    }
    this.decrementLock('smssignaturelock');
    return promise;
  },

  async sendForSMSSignatureOverride({ financingRequestId, stepId, docsToSign, message }) {
    let promise = { results: undefined };
    try {
      this.incrementLock('smssignaturelock');
      const url = `financing-requests/${financingRequestId}/steps/${stepId}/docs/signatures/via-sms/override/${
        message ? message : 'accept'
      }`;
      promise = await FetchHelper.fetchJson(url, {
        method: 'POST',
        body: {
          docs: docsToSign,
        },
      });
      if (promise.results.length < 0) {
        promise.results = [];
        this.dispatch({
          type: 'handleRequestError',
          data: { promise, defaultErrorMessage: promise.fieldErrors[0] },
        });
      }
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: { error, defaultErrorMessage: 'Could not send docs for signature' },
      });
    }
    this.decrementLock('smssignaturelock');
    return promise;
  },

  async deleteSMSSignatureRequest({ stepSku }) {
    let customer = this.getStoreVal('customer');
    let promise = { results: undefined };
    try {
      this.incrementLock('smssignaturelock');
      const url = `customers/${customer.id}/steps/${stepSku}/request-funding`;
      promise = await FetchHelper.fetchJson(url, { method: 'DELETE', version: '/v3', body: {} });
      if (promise.results.length < 0) {
        promise.results = [];
        this.dispatch({
          type: 'handleRequestError',
          data: { promise, defaultErrorMessage: promise.fieldErrors[0] },
        });
      }
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: { error, defaultErrorMessage: 'Could not delete signature request' },
      });
    }
    this.decrementLock('smssignaturelock');
    return promise;
  },

  setRTDRCurrentDecisioningDocSku(data) {
    this.setStoreVal('currentDecisioningDocSku', data);
  },

  async requestAdditionalFiles({ customerId, externalDocSku, additionalFilesRequests }) {
    let promise;
    try {
      promise = await FetchHelper.fetchJson(
        `customers/${customerId}/financing-request-step-docs/${externalDocSku}/request-more-files`,
        {
          method: 'POST',
          version: '/v3',
          body: additionalFilesRequests,
        }
      );
      await this.dispatch({ type: 'fetchAdminFinancingRequest', data: { customerId } });
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: { error, defaultErrorMessage: 'Failed to request additional files' },
      });
    }

    return promise;
  },
};
export default AdminFRDocumentsDispatcher;
